/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const Modal = ({ isOpen, onClose, title, children, className = '', fontFamily, theme, dismissable = true, titleClassName = '' }) => {
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';

      // Handle escape key
      const handleEscape = (e) => {
        if (dismissable && e.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleEscape);

      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, onClose, dismissable]);

  if (!isOpen) { return null; }

  return createPortal(
    <div
      data-theme={theme}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        fontFamily: fontFamily || undefined
      }}
      onClick={(e) => {
        // Close modal when clicking backdrop
        if (dismissable && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={`bg-base-100 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className={`flex items-center justify-between p-6 border-b border-base-content/10 ${titleClassName}`}>
            <h3 className="text-lg font-bold text-base-content">{title}</h3>
            {dismissable && (
              <button
                type="button"
                aria-label="Close modal"
                onClick={onClose}
                className="p-1 text-base-content/40 hover:text-base-content rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}
        <div className={title ? 'p-6' : 'p-6'}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;

Modal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  fontFamily: PropTypes.string,
  theme: PropTypes.string,
  dismissable: PropTypes.bool,
  titleClassName: PropTypes.string,
};